import type { ReactNode } from 'react';
import { Trash, Pause, Play, PencilSimple, Lightning, Key, Warning, Clock, XCircle, ArrowClockwise, ArrowsClockwise, CalendarBlank } from '@phosphor-icons/react';
import { useNavigate } from 'react-router';
import { Button } from '~/components/ui/button';
import { formatSchedule, type CronJob } from '~/hooks/queries/use-crons';
import { groupScheduleList } from './reflection-group';
import { ReflectionRow } from './reflection-row';
import { useToggleCron, useDeleteCron, useCronAuthStatus, useReauthCron, useRunCronNow, type CronAuthEntry } from '~/hooks/mutations/use-cron-mutations';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useConfirm } from '~/components/confirm-dialog';
import { formatRelativeTime } from '~/lib/relative-time';
import { toast } from 'sonner';
import { Tip } from '~/components/ui/tip';
import { toggleActionFor } from './one-off';

interface CronListProps {
  crons: CronJob[];
  isLoading: boolean;
  onEdit: (cron: CronJob) => void;
  /** Set when something else on the page is scheduled (a workflow), so "Nothing
   *  scheduled" would be untrue. */
  hideEmptyState?: boolean;
  /** FRONT-2780: the One-time section's own empty state replaces the default one. */
  emptyState?: ReactNode;
}

type AuthState = 'ok' | 'missing' | 'expired' | 'failing' | 'unknown' | 'system';

/**
 * Resolve the per-cron auth state. `unknown` means the auth-status payload
 * hasn't loaded yet (or failed) — render no badge in that case so we don't
 * paint every cron as "needs auth" just because a polling request was rate-
 * limited.
 *
 * `failing` covers the trickiest case: the encrypted token row still looks
 * healthy (present, not expired by date) but the most recent fire died on an
 * auth-related error (e.g. Zitadel revoked the refresh token upstream). Without
 * this signal a broken job paints as "ok" forever.
 */
function authStateOf(
  auth: CronAuthEntry | undefined,
  loaded: boolean,
): AuthState {
  if (!loaded) return 'unknown';
  // System crons (reflect) self-auth per user — never prompt for re-auth.
  if (auth?.system) return 'system';
  if (!auth || !auth.hasAuth) return 'missing';
  if (auth.expiresAt && new Date(auth.expiresAt) < new Date()) return 'expired';
  if (auth.lastRunAuthFailed) return 'failing';
  return 'ok';
}

export function cronsNeedingAuth(
  authStatus: Record<string, CronAuthEntry> | undefined,
  loaded: boolean,
): number {
  if (!authStatus || !loaded) return 0;
  let n = 0;
  for (const entry of Object.values(authStatus)) {
    const state = authStateOf(entry, true);
    if (state === 'missing' || state === 'expired' || state === 'failing') n++;
  }
  return n;
}

export function CronList({ crons, isLoading, onEdit, hideEmptyState, emptyState }: CronListProps) {
  const navigate = useNavigate();
  const toggleMutation = useToggleCron();
  const deleteMutation = useDeleteCron();
  const { data: authStatus, isSuccess: authStatusLoaded } = useCronAuthStatus();
  const reauthMutation = useReauthCron();
  const runNowMutation = useRunCronNow();
  const confirm = useConfirm();

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-[68px] w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (!crons.length) {
    if (hideEmptyState) return null;
    if (emptyState) return <>{emptyState}</>;
    return (
      <div className="rounded-lg border border-dashed border-border py-12 text-center">
        <Clock size={28} className="mx-auto text-muted-foreground/60 mb-2" />
        <p className="text-sm font-medium text-foreground">Nothing scheduled</p>
        <p className="text-xs mt-1 text-muted-foreground">Create a cron job to automate tasks</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
      {groupScheduleList(crons).map((item) => {
        // FRONT-2439: every system reflection job renders as this one row.
        if (item.kind === 'reflection') {
          return <ReflectionRow key="reflection" crons={item.crons} times={item.times} />;
        }
        const { cron } = item;
        // `cron.system` is authoritative and present even before the
        // auth-status poll resolves, so trust it first (FRONT-226).
        const authState: AuthState = cron.system
          ? 'system'
          : authStateOf(authStatus?.[cron.id], authStatusLoaded);
        const lastRun = formatRelativeTime(cron.lastRunAt);
        const hasError = cron.lastStatus === 'failed' || cron.lastStatus === 'auth_error';
        // FRONT-836: a transient failure with a retry already scheduled DID
        // fail once — the list shouldn't look silently healthy for it, but
        // it also isn't the destructive `hasError` case since it's expected
        // to self-heal. Same distinction cron-detail.tsx's badge makes.
        const isRetrying = cron.lastStatus === 'retrying';
        // FRONT-1760: a run that completed but is flagged unverified
        // (answered without a real tool call, a delegation that never
        // reached anything, or — new here — a claimed save the tool
        // evidence doesn't back up) used to fall through this list with no
        // indicator at all, even though the detail page already badges it
        // "Unverified". Same amber tone as `isRetrying` above — not
        // destructive, but not silently healthy either.
        const isUngrounded = cron.lastStatus === 'ungrounded';

        return (
          <div
            key={cron.id}
            className="group relative flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors overflow-hidden"
          >
            {/* min-w-[220px] not min-w-0, matching the name+schedule div inside it:
                a floor here is what actually matters to flexbox — the row's
                two top-level children (this wrapper vs. the actions bar) are
                what compete for space, so a floor on a GRANDCHILD alone
                doesn't stop THIS wrapper from being squeezed toward zero
                while its over-floored child visually spills out of it
                (BUG-034 follow-up: the labeled action buttons are wide
                enough to reproduce the same collapse one level up). Raised
                from 140px to 220px (BUG-034 follow-up 2): calendar schedules
                (FRONT-551, e.g. "Monthly on the 9th at 17:10") run longer
                than the plain interval strings 140px was sized for, and were
                getting silently hard-clipped mid-token with no ellipsis. */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/crons/${cron.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/crons/${cron.id}`);
                }
              }}
              className="flex-1 min-w-[220px] flex items-center gap-4 text-left cursor-pointer focus-visible:outline-none overflow-hidden"
            >
              <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                  !cron.enabled
                    ? 'bg-muted-foreground/40'
                    : authState === 'missing' || authState === 'expired' || authState === 'failing'
                      ? 'bg-amber-500'
                      : authState === 'ok' || authState === 'system'
                        ? 'bg-success'
                        : 'bg-muted-foreground/40'
                }`}
              />

              {/* min-w-[220px] not min-w-0: the job name is the one thing on
                  this row that must never fully disappear. Without a floor,
                  a narrow viewport (e.g. the Inspector panel open) lets the
                  shrink-0 error/actions siblings claim all the space and
                  squeeze this flex-1 container to true zero width — the
                  name silently vanishes rather than truncating. Raised to
                  220px alongside the wrapper above (BUG-034 follow-up 2) so
                  the schedule subtitle below it has room for calendar-length
                  strings before truncating. */}
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 min-w-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm font-medium text-foreground truncate">{cron.name}</span>
                    </TooltipTrigger>
                    <TooltipContent>{cron.name}</TooltipContent>
                  </Tooltip>
                  {!cron.enabled && (
                    <span className="text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground/80">Paused</span>
                  )}
                  {authState === 'system' && (
                    <Tip label="System schedule — runs automatically for each user under their own session. No re-auth needed.">
                      <span
                        className="flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground"
                      >
                        <ArrowClockwise size={10} weight="fill" /> Auto
                      </span>
                    </Tip>
                  )}
                  {authState === 'missing' && (
                    <span className="flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wider text-amber-600 dark:text-amber-400">
                      <Warning size={10} weight="fill" /> Needs auth
                    </span>
                  )}
                  {authState === 'expired' && (
                    <span className="flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wider text-amber-600 dark:text-amber-400">
                      <ArrowClockwise size={10} weight="fill" /> Re-auth
                    </span>
                  )}
                  {authState === 'failing' && (
                    <Tip label={`Click to re-authorize. Last run: ${authStatus?.[cron.id]?.lastResult ?? 'auth error'}`}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          reauthMutation.mutate(cron.id, {
                            onSuccess: () =>
                              toast.success(`Re-authorized "${cron.name}"`),
                            onError: (err) =>
                              toast.error(
                                `Re-auth failed: ${(err as Error).message}`,
                              ),
                          });
                        }}
                        disabled={reauthMutation.isPending}
                        className="flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[length:var(--text-2xs)] uppercase tracking-wider text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 hover:border-amber-500/50 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40 disabled:opacity-60 disabled:cursor-wait"
                        aria-label="Re-authorize this cron"
                      >
                        <Warning size={10} weight="fill" /> Failing — re-auth
                      </button>
                    </Tip>
                  )}
                </div>
                <div className="mt-0.5 text-[length:var(--text-2xs)] text-muted-foreground truncate">
                  {formatSchedule(cron.schedule)}
                </div>
              </div>

              {/* Schedule + next-run are already spelled out in the subtitle
                  above — a healthy row shows nothing more here. Only a
                  FAILED last run earns space back (Von Restorff: the one
                  thing that differs is what gets noticed). Run count isn't
                  shown here — it's a detail-page-level stat, not a glance
                  signal for the list. */}
              {hasError && lastRun && (
                <div className="hidden sm:flex items-center gap-1.5 text-[length:var(--text-2xs)] text-destructive tabular-nums shrink-0">
                  <XCircle size={11} />
                  <Tip label={cron.lastRunAt}><span>{lastRun}</span></Tip>
                </div>
              )}
              {isRetrying && lastRun && (
                <div className="hidden sm:flex items-center gap-1.5 text-[length:var(--text-2xs)] text-amber-600 dark:text-amber-400 tabular-nums shrink-0">
                  <ArrowsClockwise size={11} />
                  <Tip label={cron.lastRunAt}><span>{lastRun}</span></Tip>
                </div>
              )}
              {isUngrounded && lastRun && (
                <div className="hidden sm:flex items-center gap-1.5 text-[length:var(--text-2xs)] text-amber-600 dark:text-amber-400 tabular-nums shrink-0">
                  <Tip label="Unverified — completed without evidence to back up its claims. Open it to see why.">
                    <Warning size={11} />
                  </Tip>
                  <Tip label={cron.lastRunAt}><span>{lastRun}</span></Tip>
                </div>
              )}
            </div>

            {/* Labeled buttons (not bare icons) — matches the detail page's
                action bar (cron-detail.tsx) so the same action always looks
                the same wherever it appears (Law of Similarity). */}
            <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  runNowMutation.mutate(cron.id, {
                    onSuccess: () =>
                      toast.success(`Triggered "${cron.name}"`, {
                        description: 'Result will appear when it finishes.',
                      }),
                    onError: (err) =>
                      toast.error(`Failed: ${(err as Error).message}`),
                  });
                }}
                tooltip="Run now (bypasses schedule)"
                className="text-muted-foreground hover:text-foreground"
              >
                <Lightning size={13} weight="fill" /> Run now
              </Button>
              {(authState === 'missing' || authState === 'expired' || authState === 'failing') && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() =>
                    reauthMutation.mutate(cron.id, {
                      onSuccess: () =>
                        toast.success(`Re-authorized "${cron.name}"`),
                      onError: (err) =>
                        toast.error(
                          `Re-auth failed: ${(err as Error).message}`,
                        ),
                    })
                  }
                  tooltip={
                    authState === 'failing'
                      ? `Re-authorize with your current session (last run: ${authStatus?.[cron.id]?.lastResult ?? 'auth error'})`
                      : 'Authorize with your current session'
                  }
                  className="text-amber-600 dark:text-amber-400 hover:text-amber-700"
                >
                  <Key size={13} weight="fill" /> Re-auth
                </Button>
              )}
              {/* FRONT-810: system schedules (reflect-N) have no owner row,
                  so the backend correctly 404s Pause/Delete on them (FRONT-130
                  ownership gate) — offering the buttons at all just produced a
                  raw "Schedule not found" toast. Hide them here, same
                  `cron.system` pattern as the Auto badge / Re-auth skip above. */}
              {!cron.system && (
                <>
                  {/* FRONT-2780: a paused one-time job whose time passed can't
                      be resumed (it would run at once) — it gets a new time. */}
                  {toggleActionFor(cron, Date.now()) === 'new-time' ? (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onEdit(cron)}
                      tooltip="Its time has passed. Pick a new one."
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <CalendarBlank size={13} /> New time
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() =>
                        toggleMutation.mutate({ id: cron.id, enabled: !cron.enabled })
                      }
                      tooltip={cron.enabled ? 'Pause' : 'Resume'}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {cron.enabled ? <Pause size={13} weight="fill" /> : <Play size={13} weight="fill" />}
                      {cron.enabled ? 'Pause' : 'Resume'}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => onEdit(cron)}
                    tooltip="Edit"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <PencilSimple size={13} /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Delete "${cron.name}"?`,
                        description: 'This cannot be undone.',
                        confirmText: 'Delete',
                        destructive: true,
                      });
                      if (ok) deleteMutation.mutate(cron.id);
                    }}
                    tooltip="Delete"
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash size={13} /> Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
